import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiZap, FiClock, FiCheckCircle, FiAlertCircle, FiCoffee, 
  FiNavigation, FiFilter, FiRefreshCw, FiLoader, FiTerminal,
  FiChevronRight, FiChevronLeft, FiPlus
} = FiIcons;

const steps = [
  { id: 1, title: 'Morning Sync', desc: 'System wakes up 30m before departure to scan transit lines and weather.', icon: FiZap },
  { id: 2, title: 'Route Prep', desc: 'AI calculates the 3 fastest routes based on real-time delay telemetry.', icon: FiNavigation },
  { id: 3, title: 'Routine Audit', desc: 'Verification of departure checklist items (NFC pass, gear, coffee).', icon: FiCheckCircle },
  { id: 4, title: 'Station Arrival', desc: 'Automatic coffee ordering via Station Cafe API as you pass the mile marker.', icon: FiCoffee },
  { id: 5, title: 'Transit Phase', desc: 'Active commute tracking with multi-modal transfer alerts.', icon: FiClock },
  { id: 6, title: 'Arrival Log', desc: 'Commute metrics recorded: $ saved, carbon offset, and time efficiency.', icon: FiTerminal }
];

const sampleCommutes = [
  { id: 'TX-992', date: '2024-05-10', route: 'Cyberbus 204', cost: '$4.50', status: 'Success', time: '42m' },
  { id: 'TX-993', date: '2024-05-11', route: 'Metro Line G', cost: '$3.20', status: 'Optimal', time: '38m' },
  { id: 'TX-994', date: '2024-05-12', route: 'Cyberbus 204', cost: '$4.50', status: 'Delayed', time: '55m' },
  { id: 'TX-995', date: '2024-05-13', route: 'Walk + Rail', cost: '$2.80', status: 'Success', time: '45m' },
];

export default function Demo() {
  const [activeStep, setActiveStep] = useState(0);
  const [simState, setSimState] = useState('success'); // before, in-progress, success, empty, error
  const [filter, setFilter] = useState('all');

  const filteredCommutes = filter === 'all' ? sampleCommutes : sampleCommutes.filter(c => c.status.toLowerCase() === filter);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="space-y-12 pb-12"
    >
      <header className="mb-8">
        <h2 className="text-3xl font-bold text-neutral-100 flex items-center gap-3">
          Demo / How It Works
          <span className="text-xs font-mono text-emerald-500/50 bg-emerald-500/5 px-2 py-1 rounded border border-emerald-500/10">STABLE_V2</span>
        </h2>
        <p className="text-neutral-400 mt-1 font-mono text-sm max-w-xl">
          Explore the CalmCommute ecosystem. This interface simulates the full user journey from morning protocols to data analytics.
        </p>
      </header>

      {/* Section: Try this flow */}
      <section className="space-y-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-px flex-grow bg-neutral-800"></div>
          <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-400">Try this flow</h3>
          <div className="h-px flex-grow bg-neutral-800"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-1 space-y-4">
            {steps.map((step, index) => (
              <motion.button
                key={step.id}
                onClick={() => setActiveStep(index)}
                className={`w-full flex items-center gap-4 p-4 rounded-xl border text-left transition-all ${
                  activeStep === index 
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-100' 
                    : 'bg-neutral-900/30 border-neutral-800/50 text-neutral-500 hover:border-neutral-700'
                }`}
                whileHover={{ x: 5 }}
              >
                <div className={`p-2 rounded-lg ${activeStep === index ? 'bg-emerald-500/20' : 'bg-neutral-800'}`}>
                  <SafeIcon icon={step.icon} className="text-lg" />
                </div>
                <div>
                  <h4 className="font-medium text-sm">{step.title}</h4>
                  <p className="text-[10px] uppercase font-mono tracking-wider opacity-60">Step 0{step.id}</p>
                </div>
                {activeStep === index && <SafeIcon icon={FiChevronRight} className="ml-auto" />}
              </motion.button>
            ))}
          </div>

          <div className="lg:col-span-2 bg-neutral-900/40 border border-neutral-800 rounded-3xl p-8 relative overflow-hidden min-h-[400px] flex flex-col">
            <div className="absolute top-0 right-0 p-8 opacity-5">
              <SafeIcon icon={steps[activeStep].icon} className="text-[12rem]" />
            </div>
            
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="relative z-10 flex-grow"
              >
                <span className="text-xs font-mono text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-full mb-6 inline-block">
                  PROTOCOL_PHASE_0{steps[activeStep].id}
                </span>
                <h3 className="text-4xl font-bold text-neutral-100 mb-4">{steps[activeStep].title}</h3>
                <p className="text-lg text-neutral-400 leading-relaxed mb-8 max-w-lg">
                  {steps[activeStep].desc}
                </p>

                <div className="grid grid-cols-2 gap-4 mt-auto">
                  <div className="p-4 bg-neutral-950/50 border border-neutral-800 rounded-2xl">
                    <p className="text-[10px] font-mono text-neutral-500 uppercase mb-2">Primary Action</p>
                    <button className="w-full py-2 bg-emerald-500 text-neutral-950 rounded-lg font-bold text-xs uppercase tracking-tighter hover:bg-emerald-400 transition-colors">
                      Execute Step
                    </button>
                  </div>
                  <div className="p-4 bg-neutral-950/50 border border-neutral-800 rounded-2xl">
                    <p className="text-[10px] font-mono text-neutral-500 uppercase mb-2">System Override</p>
                    <button className="w-full py-2 border border-neutral-700 text-neutral-400 rounded-lg font-bold text-xs uppercase tracking-tighter hover:bg-neutral-800 transition-colors">
                      Manual Input
                    </button>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
            
            <div className="flex justify-between mt-8 pt-8 border-t border-neutral-800">
              <button 
                onClick={() => setActiveStep(prev => Math.max(0, prev - 1))}
                className="p-2 text-neutral-500 hover:text-neutral-200 disabled:opacity-30"
                disabled={activeStep === 0}
              >
                <SafeIcon icon={FiChevronLeft} className="text-2xl" />
              </button>
              <div className="flex gap-2">
                {steps.map((_, i) => (
                  <div key={i} className={`h-1.5 w-8 rounded-full transition-all ${activeStep === i ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-neutral-800'}`}></div>
                ))}
              </div>
              <button 
                onClick={() => setActiveStep(prev => Math.min(steps.length - 1, prev + 1))}
                className="p-2 text-neutral-500 hover:text-neutral-200 disabled:opacity-30"
                disabled={activeStep === steps.length - 1}
              >
                <SafeIcon icon={FiChevronRight} className="text-2xl" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Sample Data */}
      <section className="space-y-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-px flex-grow bg-neutral-800"></div>
          <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-400">Sample Data</h3>
          <div className="h-px flex-grow bg-neutral-800"></div>
        </div>

        <div className="bg-neutral-900/30 border border-neutral-800 rounded-2xl overflow-hidden">
          <div className="p-4 bg-neutral-950/50 border-b border-neutral-800 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <SafeIcon icon={FiFilter} className="text-neutral-500" />
              <div className="flex gap-2">
                {['all', 'success', 'delayed'].map(f => (
                  <button 
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`text-[10px] font-mono uppercase px-2 py-1 rounded transition-colors ${
                      filter === f ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'text-neutral-600 hover:text-neutral-400'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
            <button className="text-emerald-500 p-1 hover:bg-emerald-500/10 rounded transition-colors">
              <SafeIcon icon={FiPlus} />
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm font-mono">
              <thead className="bg-neutral-950/20 text-neutral-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-4 font-medium">Log ID</th>
                  <th className="px-6 py-4 font-medium">Date</th>
                  <th className="px-6 py-4 font-medium">Route Segment</th>
                  <th className="px-6 py-4 font-medium">Efficiency</th>
                  <th className="px-6 py-4 font-medium">Cost</th>
                  <th className="px-6 py-4 font-medium text-right">State</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800/50">
                {filteredCommutes.map((c) => (
                  <tr key={c.id} className="hover:bg-neutral-800/20 transition-colors group">
                    <td className="px-6 py-4 text-emerald-500/70 group-hover:text-emerald-400">{c.id}</td>
                    <td className="px-6 py-4 text-neutral-400">{c.date}</td>
                    <td className="px-6 py-4 text-neutral-200">{c.route}</td>
                    <td className="px-6 py-4 text-neutral-400">{c.time}</td>
                    <td className="px-6 py-4 text-neutral-200 font-bold">{c.cost}</td>
                    <td className="px-6 py-4 text-right">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${
                        c.status === 'Success' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' :
                        c.status === 'Optimal' ? 'bg-blue-500/10 text-blue-500 border-blue-500/20' :
                        'bg-amber-500/10 text-amber-500 border-amber-500/20'
                      }`}>
                        {c.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Section: Simulated States */}
      <section className="space-y-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-px flex-grow bg-neutral-800"></div>
          <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-400">Simulated States</h3>
          <div className="h-px flex-grow bg-neutral-800"></div>
        </div>

        <div className="flex flex-wrap gap-3 mb-6">
          {['before', 'in-progress', 'success', 'empty', 'error'].map(state => (
            <button 
              key={state}
              onClick={() => setSimState(state)}
              className={`px-4 py-2 rounded-xl border text-xs font-mono uppercase transition-all ${
                simState === state 
                  ? 'bg-neutral-100 text-neutral-950 border-neutral-100 shadow-[0_0_15px_rgba(255,255,255,0.2)]' 
                  : 'bg-neutral-900/50 border-neutral-800 text-neutral-500 hover:border-neutral-700'
              }`}
            >
              System: {state}
            </button>
          ))}
        </div>

        <div className="min-h-[240px] bg-neutral-900/40 border border-neutral-800 rounded-2xl p-8 flex items-center justify-center text-center">
          <AnimatePresence mode="wait">
            {simState === 'before' && (
              <motion.div key="before" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                <div className="w-16 h-16 bg-neutral-800 rounded-full flex items-center justify-center mx-auto mb-4 border border-neutral-700">
                  <SafeIcon icon={FiZap} className="text-2xl text-neutral-500" />
                </div>
                <h4 className="text-lg font-bold">Awaiting Protocol Initialization</h4>
                <p className="text-neutral-500 max-w-xs mx-auto text-sm">Tap the "Initialize" button in the control panel to begin morning sync.</p>
              </motion.div>
            )}

            {simState === 'in-progress' && (
              <motion.div key="in-progress" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                <SafeIcon icon={FiLoader} className="text-4xl text-emerald-500 animate-spin mx-auto mb-4" />
                <h4 className="text-lg font-bold">Synchronizing Neural Grid...</h4>
                <p className="text-neutral-500 max-w-xs mx-auto text-sm font-mono uppercase">Scanning Line 4 // Fetching Cloud Telemetry</p>
              </motion.div>
            )}

            {simState === 'success' && (
              <motion.div key="success" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-emerald-500/30">
                  <SafeIcon icon={FiCheckCircle} className="text-2xl text-emerald-400" />
                </div>
                <h4 className="text-lg font-bold text-emerald-100">System Synced Successfully</h4>
                <p className="text-neutral-500 max-w-xs mx-auto text-sm">All transit lanes operational. Departure protocol is now active.</p>
              </motion.div>
            )}

            {simState === 'empty' && (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                <div className="w-16 h-16 bg-neutral-950 rounded-full flex items-center justify-center mx-auto mb-4 border border-dashed border-neutral-800">
                  <SafeIcon icon={FiRefreshCw} className="text-2xl text-neutral-700" />
                </div>
                <h4 className="text-lg font-bold text-neutral-400">No Data Logs Found</h4>
                <p className="text-neutral-500 max-w-xs mx-auto text-sm">Your commute history is currently clear. Complete a trip to see analytics.</p>
              </motion.div>
            )}

            {simState === 'error' && (
              <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-500/30">
                  <SafeIcon icon={FiAlertCircle} className="text-2xl text-red-400" />
                </div>
                <h4 className="text-lg font-bold text-red-200">Critical Network Failure</h4>
                <p className="text-neutral-500 max-w-xs mx-auto text-sm">Unable to reach Transit Hub API. Please check your system connectivity.</p>
                <button className="text-xs font-mono text-red-400 underline decoration-red-400/30 underline-offset-4">Retry Connection</button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      <footer className="pt-12 border-t border-neutral-900 text-center">
        <p className="text-[10px] text-neutral-600 font-mono uppercase tracking-[0.3em]">
          Made by LingLink AI and DigLab AI
        </p>
      </footer>
    </motion.div>
  );
}